<template>
  <div class="cylinder-specification-container">
    <ValidationForm
      ref="validationFormSpecification"
      @success="saveHydraulicDataAndProceed"
      :get-form="getForm"
    >
      <div class="cylinder-specification-container__headline">
        <h2>
          {{ $tv("cylinderSpecificationHeadline", "hydraulicSelector") }}
        </h2>
      </div>

      <div class="cylinder-specification-container__description">
        <p class="cylinder-specification-container__description">
          {{
            !hideImageLayerAndButtons
              ? $tv("cylinderSpecificationDescription", "hydraulicSelector")
              : $tv("replacementOrderDescription", "hydraulicSelector")
          }}
        </p>
      </div>

      <div
        v-if="!hideImageLayerAndButtons"
        class="cylinder-specification-container__image-layer"
      >
        <ImageLayer
          :image-sources="HYDRAULIC_SELECTOR_CYLINDER_IMAGES"
          :active-image-index="current"
          :img-width="'47.75rem'"
          :img-height="'10.5rem'"
        />
      </div>

      <div class="cylinder-specification-container__cards">
        <div
          class="card"
          :boolWidth="true"
        >
          <div class="card-content">
            <h3 class="h4">
              {{ $tv("applicationAreaTitle", "hydraulicSelector") }}
            </h3>
            <div class="input-wrapper">
              <DropDown
                :data-items="keys"
                v-model="applicationArea"
                :data-item-key="'value'"
                :text-field="'text'"
                @change="handleApplicationAreaChange"
              />
            </div>

            <Checkbox
              :checked="!!applicationAreaUnknown"
              :label="$tv('applicationAreaCheckbox', 'hydraulicSelector')"
              @change="handleCheckboxChange"
            />
          </div>
        </div>

        <div
          class="card"
          :boolWidth="true"
        >
          <div class="cylinder-data-headline">
            <h3 class="h4">
              {{ $tv("cylinderDataTitle", "hydraulicSelector") }}*
            </h3>
            <client-only>
              <ToggleSwitchSquare
                v-if="unitSwitchingEnabled"
                :is-checked="isInch"
                :label-right="i18n.tv('inch', 'unitsOfMeasure')"
                :label-left="i18n.tv('mm', 'unitsOfMeasure')"
                @change-value="hydraulicSelectorStore.setUnitLength($event ? 'inch' : 'mm'); handleUnitChange($event ? 'inch' : 'mm')"
              />
            </client-only>
          </div>
          <div class="card-content">
            <div class="input-wrapper">
              <label for="cylinderSpecRodDiameter">
                {{ $tv({messageKey: 'rodDiameterLabelWithUnit', messageValues: {unit: currentUnitLengthLabel},}, 'hydraulicSelector') }}
              </label>
              <NumericInput
                id="cylinderSpecRodDiameter"
                ref="validationFormRodDiameter"
                v-model="rodDiameter"
                :format="isInch ? 'n3' : 'n2'"
                name="rodDiameter"
                @focus="handleFocus('rod')"
                @blur="handleBlur"
                @change="$emit('rodDiameterChanged', rodDiameter)"
                :rules="validationRules?.rodDiameter"
              />
            </div>
            <div class="input-wrapper">
              <label for="cylinderSpecCylinderDiameter">
                {{ $tv({messageKey: 'cylinderDiameterLabelWithUnit', messageValues: {unit: currentUnitLengthLabel},}, 'hydraulicSelector') }}
              </label>
              <NumericInput
                id="cylinderSpecCylinderDiameter"
                ref="validationFormCylinerDiameter"
                v-model="cylinderDiameter"
                :format="isInch ? 'n3' : 'n2'"
                name="cylinderDiameter"
                @focus="handleFocus('cylinder')"
                @blur="handleBlur"
                @change="$emit('cylinderDiameterChanged', cylinderDiameter)"
                :rules="validationRules?.cylinderDiameter"
              />
            </div>
          </div>
        </div>

        <div
          class="card"
          :boolWidth="true"
        >
          <h3 class="sealing-reqs-headline h4">
            {{ $tv("sealsNeedsTitle", "hydraulicSelector") }}*
          </h3>
          <div class="card-content">
            <div>
              <input
                id="newCylinderDesign"
                type="radio"
                value="newCylinderDesign"
                v-model="selectedRadio"
                @change="onRadioChange"
              >
              <label for="newCylinderDesign"> {{ $tv("newCylinderDesign", "hydraulicSelector") }}</label>
            </div>

            <div>
              <input
                id="sparePartCylinder"
                type="radio"
                value="sparePartCylinder"
                v-model="selectedRadio"
                @change="onRadioChange"
              >
              <label for="sparePartCylinder"> {{ $tv("sparePartCylinder", "hydraulicSelector") }}</label>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!hideImageLayerAndButtons"
        class="cylinder-specification-container__buttons"
      >
        <ReactiveButton
          :is-primary-button="false"
          @click="resetData"
          :button-text="$tv('resetValues', 'hydraulicSelector')"
        />
        <ReactiveButton
          :disabled="isButtonDisabled"
          :button-text="$tv('showProducts', 'hydraulicSelector')"
          type="submit"
        />
      </div>
    </ValidationForm>
  </div>
</template>
<script setup lang="ts">
import NumericInput from '@/components/input-components/NumericInput.vue'
import DropDown from '@/components/input-components/DropDown.vue'
import Checkbox from '@/components/input-components/Checkbox.vue'
import ImageLayer from '@/components/hydraulic-selector/ImageLayer.vue'
import ValidationForm from '@/components/input-components/ValidationForm.vue'
import {useHydraulicSelectorStore} from '@/store/hydraulic-selector'
import {ref, watch, computed, onMounted} from 'vue'
import type {inputs} from '@/types/hydraulic-selector-store.d'
import {HYDRAULIC_SELECTOR_CYLINDER_IMAGES} from '~/assets/js/constants'
import useI18nExtended from '~/composables/usei18nExtended'
import {newEmptyPostBody} from '@/types/helper.d'
import {minMaxNumberValidationRule, greaterThanValidationRule, lessThanValidationRule} from '~/assets/js/validations/common_validation_rules'
import {useHydraulicValidationHelper} from '~/composables/hydraulic_validation_helper'
import {useHydraulicHelper} from '~/composables/hydraulic_helper'
import ToggleSwitchSquare from '~/components/input-components/ToggleSwitchSquare.vue'
import {storeToRefs} from 'pinia'

const props = defineProps({
  hideImageLayerAndButtons: {
    type: Boolean,
    default: false
  },
  selectedRadioProp: {
    type: String,
    required: false,
    default() {
      return ''
    }
  }
})

const current = ref('default')
const sortedList = ref([])
const keys = ref([])
const applicationArea: Ref<string | null | undefined> = ref(null)
const rodDiameter = ref<number|null>(null)
const cylinderDiameter = ref<number|null>(null)
const validationFormSpecification = ref(null)
const applicationAreaUnknown = ref(false)
const hydraulicSelectorStore = useHydraulicSelectorStore()
const {currentUnitLengthLabel, metadataForLength, convertValue} = useHydraulicHelper()
const {selectedRadioProp} = toRefs(props)
const selectedRadio = ref(selectedRadioProp.value)
const {isInch, unitSwitchingEnabled} = storeToRefs(useHydraulicSelectorStore())
const emits = defineEmits(['radio-changed', 'update:selectedRadioProp', 'rodDiameterChanged', 'cylinderDiameterChanged'])

const revalidate = async () => {
  return await validationFormSpecification.value?.validate()
}

defineExpose({
  revalidate
})

const {getMinValue, getMaxValue} = useHydraulicValidationHelper()
const initialData = {
  rodDiameter: null,
  cylinderDiameter: null,
  applicationAreaUnknown: false,
  applicationArea: null,
  selectedRadio: 'newCylinderDesign'
}

const handleUnitChange = (unit: string) => {
  rodDiameter.value = convertValue(unit, rodDiameter.value)
  cylinderDiameter.value = convertValue(unit, cylinderDiameter.value)
}
const onRadioChange = (event) => {
  const selectedValue = event.target.value
  if (selectedValue) {
    emits('radio-changed', selectedValue)
    emits('update:selectedRadioProp', selectedValue)
  }
}
const resetData = () => {
  rodDiameter.value = initialData.rodDiameter
  cylinderDiameter.value = initialData.cylinderDiameter
  applicationAreaUnknown.value = initialData.applicationAreaUnknown
  if (keys.value.length > 0) {
    applicationArea.value = {value: keys.value[0].value, text: i18n.tv(keys.value[0].value, 'hydraulicSelector')}
  }
  selectedRadio.value = initialData.selectedRadio
  hydraulicSelectorStore.setCurrentData(newEmptyPostBody())
  hydraulicSelectorStore.checkboxes = {}
  hydraulicSelectorStore.initializeUnits()
}

const app = useNuxtApp()

const saveHydraulicDataAndProceed = () => {
  const hydraulicData: inputs['BasicInputs'] = {
    rodDiameter: rodDiameter.value,
    outerDiameter: cylinderDiameter.value,
    applicationArea: applicationArea.value?.value,
    sealsNeeds: selectedRadio.value,
    applicationAreaUnknown: applicationAreaUnknown.value
  }

  hydraulicSelectorStore.basicInput = hydraulicData
  hydraulicSelectorStore.setCurrentDataMultiple([
    {key: 'wiper.rodDiameter', value: rodDiameter.value},
    {key: 'rodSeal.rodDiameter', value: rodDiameter.value},
    {key: 'rodGuide.rodDiameter', value: rodDiameter.value},
    {key: 'pistonSeal.boreDiameter', value: cylinderDiameter.value},
    {key: 'pistonGuide.boreDiameter', value: cylinderDiameter.value},
    {key: 'bufferSeal.rodDiameter', value: rodDiameter.value}
  ])
  if (selectedRadio.value === 'newCylinderDesign') {
    navigateTo(app.$globalization.localePath('/hydraulic-selector/new-construction'))
  } else if (selectedRadio.value === 'sparePartCylinder') {
    navigateTo(app.$globalization.localePath('/hydraulic-selector/replacement'))
  }
}

const handleCheckboxChange = () => {
  applicationAreaUnknown.value = !applicationAreaUnknown.value
  if (applicationAreaUnknown.value) {
    applicationArea.value = null
  } else {
    const defaultValue = keys.value.length > 0 ? keys.value[0] : null
    applicationArea.value = defaultValue
  }
}

const handleApplicationAreaChange = (event: any) => {
  const selectedValue = event.target.value
  applicationArea.value = selectedValue
  applicationAreaUnknown.value = !selectedValue
}

const i18n = useI18nExtended()

watch(
  () => hydraulicSelectorStore.presets,
  (newPresets) => {
    if (newPresets && newPresets.length > 0) {
      const newSortedList = newPresets
        .map((s) => ({
          key: s.applicationPreset,
          order: s.displayOrder
        }))
        .sort((a, b) => a.order - b.order)
      sortedList.value = newSortedList
      keys.value = newSortedList.map((item) => ({value: item.key, text: i18n.tv(item.key, 'hydraulicSelector')}))
      if (!applicationAreaUnknown.value && keys.value.length > 0) {
        applicationArea.value = keys.value[0]
      }
    }
  },
  {immediate: true}
)
watch(
  () => props.selectedRadioProp, (newVal) => {
    selectedRadio.value = newVal
  },
  {immediate: true}
)

const handleFocus = (type: any) => {
  current.value = type
}

const handleBlur = () => {
  current.value = 'default'
}

const getForm = () => {
  return {
    rodDiameter: rodDiameter.value,
    cylinderDiameter: cylinderDiameter.value
  }
}

const isButtonDisabled = computed(() => {
  const isRadioSelected =
    selectedRadio.value === 'newCylinderDesign' ||
    selectedRadio.value === 'sparePartCylinder'
  const isDimensionSelected = rodDiameter.value || cylinderDiameter.value
  return !(isRadioSelected && isDimensionSelected)
})

const validationRules = computed(() => {
  return {
    rodDiameter: [
      minMaxNumberValidationRule(i18n.tv('rodDiameterLabel', 'hydraulicSelector'), getMinValue(metadataForLength.value?.validation?.wiper?.rodDiameter, null), getMaxValue(metadataForLength.value?.validation?.wiper?.rodDiameter, null), true),
      lessThanValidationRule(i18n.tv('rodDiameterLabel', 'hydraulicSelector'), i18n.tv('cylinderDiameterLabel', 'hydraulicSelector'), cylinderDiameter.value, true)
    ],
    cylinderDiameter: [
      minMaxNumberValidationRule(i18n.tv('cylinderDiameterLabel', 'hydraulicSelector'), getMinValue(metadataForLength.value?.validation?.pistonSeal?.boreDiameter, null), getMaxValue(metadataForLength.value?.validation?.pistonSeal?.boreDiameter, null), true),
      greaterThanValidationRule(i18n.tv('cylinderDiameterLabel', 'hydraulicSelector'), i18n.tv('rodDiameterLabel', 'hydraulicSelector'), rodDiameter.value, true)
    ]
  }
})

onMounted(() => {
  applicationArea.value = {value: hydraulicSelectorStore.basicInput.applicationArea, text: i18n.tv(hydraulicSelectorStore.basicInput.applicationArea, 'hydraulicSelector')}
  selectedRadio.value = hydraulicSelectorStore.basicInput.sealsNeeds
  rodDiameter.value = hydraulicSelectorStore.basicInput.rodDiameter
  cylinderDiameter.value = hydraulicSelectorStore.basicInput?.outerDiameter
  applicationAreaUnknown.value = hydraulicSelectorStore.basicInput?.applicationAreaUnknown ?? null

  useNuxtApp().$eventbus.on('resetHydraulicData', () => resetData())
})

onUnmounted(() => {
  useNuxtApp().$eventbus.off('resetHydraulicData')
})
</script>
<style lang="scss">
.cylinder-specification-container {
  background-color: $color__white;

  &__headline {
    padding: .5rem 0;

    h2 {
      margin: 0;
    }
  }

  &__description {
    font-size: $base-text-font-size;
    font-weight: $base-text-font-weight-light;
    font-style: normal;
    line-height: 1.1375rem;

    p {
      margin-top: 0;
    }
  }

  &__image-layer {
    display: flex;
    justify-content: center;
    padding: 1rem 0;
    align-items: center;
    gap: .625rem;
  }

  &__cards {
    display: grid;
    grid-template-columns: 33.3333% 33.3333% 33.3333%;
    grid-template-rows: auto;

    .card:first-child {
      margin-left: 0;
    }

    .card:last-child {
      margin-right: 0;
    }

    .card {
      display: flex;
      padding: 1rem;
      flex-direction: column;
      margin: 1.25rem .625rem 0;
      align-items: flex-start;
      flex: 1 0 0;
      align-self: stretch;

      .cylinder-data-headline {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        margin-bottom: .5rem;
      }

      .sealing-reqs-headline {
        margin-bottom: 1rem;
      }

      &.first-card {
        margin-left: 0;
      }

      label {
        font-size: $base-text-font-size;
        font-style: normal;
        font-weight: $base-text-font-weight-light;
        padding-bottom: .38rem;
      }

      h3 {
        margin: 0;
      }

      .card-content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
        flex: 1 0 0;
        align-self: stretch;
      }

      .input-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .k-input-value-text {
          font-weight: $base-text-font-weight-semi-bold;
        }
      }
    }
  }

  &__buttons {
    display: flex;
    padding: 2rem 0 1.25rem;
    justify-content: flex-end;
    align-items: center;
    gap: 1.25rem;
    align-self: stretch;

    .k-button.k-button-solid-base {
      background-color: $color__content--background__secondary;
    }

    .k-button-solid-base {
      color: $color__primary;
    }

    .k-button.k-button-solid {
      font-weight: $base-text-font-weight-semi-bold;
    }
  }

  :deep(.k-label) {
    font-size: $base-text-font-size;
    font-style: normal;
    font-weight: $base-text-font-weight-light;
  }
}
</style>
